import { useEffect, useState, useRef, useMemo } from "react";
import { Play, Pause, RotateCcw, Film, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/lib/useApi";
import { useLang } from "@/context/AppContext";
import { Slider } from "@/components/ui/slider";
import { REACTION_ICONS } from "@/components/BroadcastLive";
import { computeHighlights, HighlightMarkers, HighlightList } from "@/components/ReplayHighlights";

export const fmtT = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const SPEEDS = [1, 2, 4, 8];

export const shareMoment = async (cid, bid, sec, t) => {
  const url = `${window.location.origin}/app/communities/${cid}?replay=${bid}&t=${Math.floor(sec)}`;
  try { await navigator.clipboard.writeText(url); toast.success(t("link_copied")); } catch { toast.info(url); }
  return url;
};

const NO_RECORDING = { available: false, status: "none" };

const useReplayData = (bid) => {
  const { data: r } = useApi(`/broadcasts/${bid}/replay`, { toastErrors: true });
  const { data: rec } = useApi(`/broadcasts/${bid}/recording`, { fallback: NO_RECORDING });
  return { r, rec };
};

const useClock = ({ active, setPlaying, speed, duration, setPos }) => {
  useEffect(() => {
    if (!active) return undefined;
    const h = setInterval(() => setPos((p) => {
      const n = Math.min(duration, p + 0.25 * speed);
      if (n >= duration) setPlaying(false);
      return n;
    }), 250);
    return () => clearInterval(h);
  }, [active, speed, duration, setPos, setPlaying]);
};

const useFloats = (reactions, pos, last) => {
  const [floats, setFloats] = useState([]);
  useEffect(() => {
    if (!reactions) return;
    const passed = reactions.filter((x) => x.t > last.current && x.t <= pos).slice(-12);
    last.current = pos;
    if (!passed.length) return;
    const fs = passed.map((x) => ({ id: Math.random(), emoji: x.emoji, x: 10 + Math.random() * 80 }));
    setFloats((xs) => [...xs, ...fs]);
    setTimeout(() => setFloats((xs) => xs.filter((f) => !fs.includes(f))), 2200);
  }, [pos, reactions, last]);
  return floats;
};

const RecordingView = ({ rec, vid, onMeta, onTime, onEnded }) => {
  const { t } = useLang();
  if (!rec) return null;
  if (rec.available) {
    return <video ref={vid} src={rec.url} onLoadedMetadata={onMeta} onTimeUpdate={onTime} onEnded={onEnded} playsInline preload="metadata"
      className="mb-4 aspect-video w-full rounded-md bg-black" data-testid="replay-video" />;
  }
  return (
    <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid="replay-recording-status">
      <Film className="h-3.5 w-3.5" />{t(rec.status === "none" ? "recording_none" : "recording_processing")}
    </p>
  );
};

const ReplayComments = ({ comments, hostId, ended }) => {
  const { t } = useLang();
  const list = useRef(null);
  const stick = useRef(true);
  useEffect(() => { if (list.current && stick.current) list.current.scrollTop = list.current.scrollHeight; }, [comments.length]);
  const onScroll = (e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; };
  return (
    <div ref={list} onScroll={onScroll} className="h-56 space-y-2 overflow-y-auto pr-1" data-testid="replay-comments">
      {!comments.length && <p className="text-sm text-slate-500">{t("chat_empty")}</p>}
      {comments.map((c) => (
        <p key={c.id} className="rise text-sm" data-testid={`replay-comment-${c.id}`}>
          <span className="mr-2 font-mono text-[10px] text-slate-600">{fmtT(c.t)}</span>
          <span className={`font-semibold ${c.user_id === hostId ? "text-yellow-300" : "text-cyan-300"}`}>{c.name}</span>
          <span className="ml-2 text-slate-200">{c.text}</span>
        </p>
      ))}
      {ended && <p className="pt-2 text-center font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid="replay-ended">{t("replay_ended")}</p>}
    </div>
  );
};

const ReactionCounts = ({ counts }) => (
  <div className="mt-3 flex flex-wrap gap-2" data-testid="replay-reaction-counts">
    {Object.entries(REACTION_ICONS).map(([k, [I, c]]) => (
      <span key={k} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-xs text-slate-300"><I className={`h-4 w-4 ${c}`} /><span className="font-mono" data-testid={`replay-count-${k}`}>{counts[k] || 0}</span></span>
    ))}
  </div>
);

const playIcon = (ended, playing) => {
  if (ended) return RotateCcw;
  return playing ? Pause : Play;
};

const PlayButton = ({ ended, playing, onClick }) => {
  const { t } = useLang();
  const Icon = playIcon(ended, playing);
  return (
    <button onClick={onClick} data-testid="replay-play-btn" title={t(playing ? "pause" : "play")}
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-emerald-500 text-slate-950 hover:opacity-90"><Icon className="h-4 w-4" /></button>
  );
};

const Controls = ({ pos, duration, playing, speed, setSpeed, onToggle, seek, highlights, jump }) => {
  const scrubValue = useMemo(() => [pos], [pos]);
  return (
    <div className="mt-4 flex items-center gap-3">
      <PlayButton ended={pos >= duration} playing={playing} onClick={onToggle} />
      <span className="w-24 font-mono text-[11px] text-slate-400" data-testid="replay-time">{fmtT(pos)} / {fmtT(duration)}</span>
      <div className="relative flex-1">
        <HighlightMarkers highlights={highlights} duration={duration} onJump={jump} />
        <Slider value={scrubValue} min={0} max={duration} step={0.5} onValueChange={([v]) => seek(v)} data-testid="replay-scrubber" />
      </div>
      <div className="flex gap-1">
        {SPEEDS.map((s) => (
          <button key={s} onClick={() => setSpeed(s)} data-testid={`replay-speed-${s}`}
            className={`rounded px-2 py-0.5 font-mono text-[11px] ${speed === s ? "bg-cyan-400/15 text-cyan-300" : "text-slate-500 hover:text-white"}`}>{s}x</button>
        ))}
      </div>
    </div>
  );
};

const useVideoSync = (vid, playing, speed, hasVideo) => {
  useEffect(() => {
    const v = vid.current;
    if (!v) return;
    v.playbackRate = speed;
    if (playing && v.paused) v.play().catch(() => {});
    if (!playing && !v.paused) v.pause();
  }, [vid, playing, speed, hasVideo]);
};

const useReplayView = (r, pos, duration) => {
  const highlights = useMemo(() => (r ? computeHighlights(r.reactions, duration) : []), [r, duration]);
  const shown = useMemo(() => (r ? r.comments.filter((c) => c.t <= pos) : []), [r, pos]);
  const counts = useMemo(() => (r ? r.reactions.filter((x) => x.t <= pos).reduce((m, x) => ({ ...m, [x.emoji]: (m[x.emoji] || 0) + 1 }), {}) : {}), [r, pos]);
  return { highlights, shown, counts };
};

const useTimeline = ({ start, duration, offset, vid }) => {
  const [pos, setPos] = useState(start);
  const [playing, setPlaying] = useState(true);
  const last = useRef(start);
  const startedVideo = useRef(false);
  const seek = (v) => { last.current = v; setPos(v); if (vid.current) vid.current.currentTime = Math.max(0, v - offset); };
  const jump = (h) => { seek(Math.max(0, h.start - 2)); setPlaying(true); };
  const toggle = () => {
    if (pos < duration) return setPlaying(!playing);
    seek(0);
    return setPlaying(true);
  };
  const onMeta = () => {
    if (!vid.current || startedVideo.current) return;
    startedVideo.current = true;
    vid.current.currentTime = Math.max(0, start - offset);
  };
  const onTime = () => { if (vid.current) setPos(Math.min(duration, vid.current.currentTime + offset)); };
  return { pos, setPos, playing, setPlaying, last, seek, jump, toggle, onMeta, onTime };
};

export const ReplayPlayer = ({ bid, cid, start = 0 }) => {
  const { t } = useLang();
  const { r, rec } = useReplayData(bid);
  const [speed, setSpeed] = useState(4);
  const vid = useRef(null);
  const duration = Math.max(1, r?.duration_s || 1);
  const hasVideo = !!rec?.available;
  const tl = useTimeline({ start, duration, offset: rec?.offset_s || 0, vid });
  const { pos, playing, setPlaying, seek, jump } = tl;
  useVideoSync(vid, playing, speed, hasVideo);
  useClock({ active: playing && !!r && !hasVideo, setPlaying, speed, duration, setPos: tl.setPos });
  const floats = useFloats(r?.reactions, pos, tl.last);
  const { highlights, shown, counts } = useReplayView(r, pos, duration);

  if (!r) return <p className="mt-3 font-mono text-xs text-slate-500">…</p>;

  return (
    <div className="relative mt-4 overflow-hidden rounded-md border border-cyan-400/20 bg-[#0B0F17] p-4" data-testid={`replay-player-${bid}`}>
      {floats.map((f) => { const [I, c] = REACTION_ICONS[f.emoji]; return <I key={f.id} className={`floater pointer-events-none absolute bottom-16 h-6 w-6 ${c}`} style={{ left: `${f.x}%` }} />; })}
      <RecordingView rec={rec} vid={vid} onMeta={tl.onMeta} onTime={tl.onTime} onEnded={() => setPlaying(false)} />
      <ReplayComments comments={shown} hostId={r.host_id} ended={pos >= duration} />
      <ReactionCounts counts={counts} />
      <Controls pos={pos} duration={duration} playing={playing} speed={speed} setSpeed={setSpeed} onToggle={tl.toggle} seek={seek} highlights={highlights} jump={jump} />
      <div className="mt-3 flex justify-end">
        <button onClick={() => shareMoment(cid, bid, pos, t)} data-testid="share-current-moment-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs text-slate-300 transition-colors hover:border-cyan-400 hover:text-cyan-300">
          <Link2 className="h-3.5 w-3.5" />{t("share_moment")} · {fmtT(pos)}
        </button>
      </div>
      <HighlightList highlights={highlights} pos={pos} onJump={jump} onShare={(h) => shareMoment(cid, bid, Math.max(0, h.start - 2), t)} />
    </div>
  );
};
